import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type { FpMessage, FpMessagePart } from "../../../schemas/codegen";
import type { MessageState, WebSocketError } from "./types";

const EMPTY_MESSAGES: FpMessage[] = [];

// Type guards for message parts
const isTextPart = (
  part: FpMessagePart,
): part is Extract<FpMessagePart, { type: "text" }> => part.type === "text";
const isToolCallPart = (
  part: FpMessagePart,
): part is Extract<FpMessagePart, { type: "tool-call" }> =>
  part.type === "tool-call";

export const useMessageStore = create<MessageState>()(
  devtools(
    (set) => ({
      messages: EMPTY_MESSAGES,
      isLoading: false,
      webSocketError: null,
      deploymentLogs: {},

      updateMessage: (messageId: string, update: Partial<FpMessage>): void =>
        set((state: MessageState) => {
          const messageIndex = state.messages.findIndex(
            (m: FpMessage) => m.id === messageId,
          );

          if (messageIndex === -1) {
            return state;
          }

          const updatedMessage: FpMessage = {
            ...state.messages[messageIndex],
            ...update,
          };

          const newMessages = [...state.messages];
          newMessages[messageIndex] = updatedMessage;

          return {
            ...state,
            messages: newMessages,
          };
        }),

      setMessages: (messages: FpMessage[]) =>
        set(() => ({
          messages,
          isLoading: false,
        })),

      addMessage: (message: FpMessage) =>
        set((state) => {
          // Check if message already exists
          const existingIndex = state.messages.findIndex(
            (msg) => msg.id === message.id,
          );

          // This is typically when the user sends a message and it gets returned from the server
          if (existingIndex >= 0) {
            // Call update message
            state.updateMessage(message.id, message);
            return state;
          }

          // Add new message with appropriate status
          const newMessage: FpMessage = {
            ...message,
            status: message.sender === "user" ? "completed" : message.status,
          };

          const newMessages = [...state.messages, newMessage];

          return {
            messages: newMessages,
          };
        }),

      appendMessageContent: (id: string, content: string) =>
        set((state) => {
          if (!content) {
            return state;
          }

          const updatedMessages = state.messages.map((msg) => {
            if (msg.id !== id) {
              return msg;
            }

            // Idempotency guard: if the last chunk was already appended, skip
            if (msg.content?.endsWith(content)) {
              return msg;
            }

            return { ...msg, content: msg.content + content };
          });

          return {
            messages: updatedMessages,
          };
        }),

      addMessagePart: (messageId: string, part: FpMessagePart, index: number) =>
        set((state) => {
          const oldMessage = state.messages.find((msg) => msg.id === messageId);
          if (!oldMessage) {
            return state;
          }

          const updatedMessages = state.messages.map((msg) => {
            if (msg.id !== messageId) {
              return msg;
            }

            // Initialize parts array if it doesn't exist
            const parts = msg.parts ? [...msg.parts] : [];

            // Create a new array with the correct length
            const newParts = new Array(Math.max(parts.length, index + 1));

            // Copy existing parts
            parts.forEach((p, i) => {
              newParts[i] = p;
            });

            // Insert the new part at the specified index
            newParts[index] = part;

            // Filter out any undefined entries
            const filteredParts = newParts.filter(
              (p): p is FpMessagePart => p !== undefined,
            );

            return { ...msg, parts: filteredParts };
          });

          const newMessage = updatedMessages.find(
            (msg) => msg.id === messageId,
          );
          if (!newMessage) {
            return state;
          }

          // Only update versions if the version info has changed

          return {
            messages: updatedMessages,
          };
        }),

      setMessageParts: (messageId: string, parts: FpMessagePart[]) =>
        set((state) => {
          const oldMessage = state.messages.find((msg) => msg.id === messageId);
          if (!oldMessage) {
            return state;
          }

          const updatedMessages = state.messages.map((msg) =>
            msg.id === messageId ? { ...msg, parts } : msg,
          );

          const newMessage = updatedMessages.find(
            (msg) => msg.id === messageId,
          );
          if (!newMessage) {
            return state;
          }

          // Only update versions if the version info has changed

          return {
            messages: updatedMessages,
          };
        }),

      updateMessagePart: (
        messageId: string,
        partId: string,
        updates: {
          content?: string;
          status?: string;
          result?: unknown;
        },
      ) =>
        set((state) => {
          const oldMessage = state.messages.find(
            (m: FpMessage) => m.id === messageId,
          );
          if (!oldMessage) {
            return state;
          }

          const updatedParts = oldMessage.parts?.map((part: FpMessagePart) => {
            // HACK - I haven't tracked down why, it's usually during failure modes when a message is interrupted for whatever reason, but sometimes parts lack IDs on the frontend (fixed after refresh)
            if (!part?.id) {
              console.error(
                "[message-store] updateMessagePart: part has no id",
                { part },
              );
              return part;
            }

            if (part.id !== partId) {
              return part;
            }

            // Only update allowed fields for each part type
            if (isTextPart(part)) {
              // Update text part content and/or status
              const updatedPart = { ...part };
              if (updates.content !== undefined) {
                const incoming = updates.content ?? "";
                if (incoming && !updatedPart.content?.endsWith(incoming)) {
                  updatedPart.content = updatedPart.content + incoming;
                }
              }
              if (updates.status !== undefined) {
                updatedPart.status = updates.status as "streaming" | "complete";
              }
              return updatedPart;
            }

            if (isToolCallPart(part)) {
              const updatedPart = { ...part };
              if (updates.status) {
                updatedPart.status = updates.status as
                  | "pending"
                  | "executing"
                  | "complete"
                  | "error";
              }
              if ("result" in updates) {
                updatedPart.result = updates.result;

                if (typeof updates === "object" && "result" in updates) {
                  updatedPart.result = updates.result;
                }
              }
              return updatedPart;
            }

            return part;
          });

          const newMessage = {
            ...oldMessage,
            parts: updatedParts,
          };

          const newMessages = state.messages.map((m: FpMessage) =>
            m.id === messageId ? newMessage : m,
          );

          return {
            messages: newMessages,
          };
        }),

      setWebSocketError: (webSocketError: WebSocketError | null) =>
        set({ webSocketError }),

      setLoading: (isLoading: boolean) => set({ isLoading }),

      clearMessages: () =>
        set({
          messages: EMPTY_MESSAGES,

          deploymentLogs: {},
          isLoading: false,
        }),

      // Deployment logs actions
      setDeploymentLogs: (
        deploymentId: string,
        stepsLogs: Record<string, string>,
      ) =>
        set((state) => {
          const now = new Date();
          const existingDeployment = state.deploymentLogs[deploymentId] || {};
          const deploymentStepLogs: Record<
            string,
            { logs: string; lastUpdated: Date }
          > = { ...existingDeployment };

          // Convert string logs to DeploymentStepLog objects, preserving existing real-time logs
          for (const [stepId, serverLogs] of Object.entries(stepsLogs)) {
            const existingStep = existingDeployment[stepId];

            if (existingStep) {
              // If we already have real-time logs, append server logs only if they're different
              // Usually server logs will be empty for new deployments, so we keep the real-time logs
              const combinedLogs = serverLogs
                ? existingStep.logs.includes(serverLogs)
                  ? existingStep.logs
                  : // TODO: Implement a better way to avoid duplicate logs.
                    // The current approach assumes that if the current logs does not include the server
                    // logs then the server logs are new and we should insert them before the
                    // existing/received logs
                    serverLogs + existingStep.logs
                : existingStep.logs;

              deploymentStepLogs[stepId] = {
                logs: combinedLogs,
                lastUpdated: existingStep.lastUpdated, // Keep the more recent timestamp
              };
            } else {
              // No existing logs, use server data
              deploymentStepLogs[stepId] = {
                logs: serverLogs,
                lastUpdated: now,
              };
            }
          }

          return {
            ...state,
            deploymentLogs: {
              ...state.deploymentLogs,
              [deploymentId]: deploymentStepLogs,
            },
          };
        }),

      appendStepLogs: (deploymentId: string, stepId: string, newLogs: string) =>
        set((state) => {
          const currentDeployment = state.deploymentLogs[deploymentId] || {};
          const currentStep = currentDeployment[stepId] || {
            logs: "",
            lastUpdated: new Date(),
          };

          return {
            ...state,
            deploymentLogs: {
              ...state.deploymentLogs,
              [deploymentId]: {
                ...currentDeployment,
                [stepId]: {
                  logs: currentStep.logs + newLogs,
                  lastUpdated: new Date(),
                },
              },
            },
          };
        }),

      clearDeploymentLogs: (deploymentId: string) =>
        set((state) => {
          const { [deploymentId]: _, ...remainingLogs } = state.deploymentLogs;
          return {
            ...state,
            deploymentLogs: remainingLogs,
          };
        }),

      clearAllDeploymentLogs: () =>
        set((state) => ({
          ...state,
          deploymentLogs: {},
        })),

      clearProjectData: () =>
        set({
          messages: EMPTY_MESSAGES,

          deploymentLogs: {},
          isLoading: false,
          webSocketError: null,
        }),
      // Legacy alias for backwards compatibility
    }),
    {
      name: "message-store",
    },
  ),
);
